import { Controller } from "@hotwired/stimulus";

export default class CommandPaletteController extends Controller {
  static override targets = ["input", "results"];
  static override values = { id: String };

  declare readonly idValue: string;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly resultsTarget: HTMLElement;
  declare readonly hasResultsTarget: boolean;
  declare dialog: HTMLDialogElement;

  override connect(): void {
    this.dialog = this.element as HTMLDialogElement;
    document.addEventListener("keydown", this.triggerKeyListener);
    this.element.addEventListener("htmx:after:swap", this.highlightFirst);
  }

  override disconnect(): void {
    document.removeEventListener("keydown", this.triggerKeyListener);
    this.element.removeEventListener("htmx:after:swap", this.highlightFirst);
  }

  private highlightFirst = (): void => {
    const first = this.resultsTarget.querySelector<HTMLAnchorElement>(
      "[data-command-palette-item]",
    );
    if (first) {
      first.classList.add("active", "bg-purple-tint");
      first
        .querySelector(":scope > div > div:first-child")
        ?.classList.replace("font-medium", "font-bold");
      first
        .querySelector(":scope > div > div:last-child")
        ?.classList.add("font-semibold");
    }
  };

  private triggerKeyListener = (event: KeyboardEvent): void => {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      this.open();
    }
  };

  open(event?: CustomEvent): void {
    if (event?.detail?.id && event.detail.id !== this.idValue) return;
    this.dialog.showModal();
    this.inputTarget.value = "";
    this.resultsTarget.innerHTML = "";
    this.inputTarget.focus();
  }

  close(): void {
    this.dialog.close();
  }

  backdropClick(event: PointerEvent): void {
    const contentRect = this.dialog
      .querySelector(".modal-box")
      ?.getBoundingClientRect();
    if (!contentRect) return;
    if (
      event.clientX < contentRect.left ||
      event.clientX > contentRect.right ||
      event.clientY < contentRect.top ||
      event.clientY > contentRect.bottom
    ) {
      this.close();
    }
  }

  inputBlur(): void {
    requestAnimationFrame(() => {
      if (!this.dialog.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  navigate(event: KeyboardEvent): void {
    if (!this.hasResultsTarget) return;
    const items = this.resultsTarget.querySelectorAll<HTMLAnchorElement>(
      "[data-command-palette-item]",
    );
    if (items.length === 0) return;

    const active = this.resultsTarget.querySelector<HTMLAnchorElement>(
      "[data-command-palette-item].active",
    );
    let activeIndex = active ? Array.from(items).indexOf(active) : -1;

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        activeIndex = activeIndex < items.length - 1 ? activeIndex + 1 : 0;
        this.setActive(items, activeIndex);
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        activeIndex = activeIndex > 0 ? activeIndex - 1 : items.length - 1;
        this.setActive(items, activeIndex);
        break;
      }
      case "Enter": {
        event.preventDefault();
        if (active) {
          this.close();
          window.location.href = active.href;
        }
        break;
      }
    }
  }

  private setActive(items: NodeListOf<HTMLAnchorElement>, index: number): void {
    for (const item of items) {
      item.classList.remove("active", "bg-purple-tint");
      item
        .querySelector(":scope > div > div:first-child")
        ?.classList.replace("font-bold", "font-medium");
      item
        .querySelector(":scope > div > div:last-child")
        ?.classList.remove("font-semibold");
    }
    const item = items[index];
    if (item) {
      item.classList.add("active", "bg-purple-tint");
      item
        .querySelector(":scope > div > div:first-child")
        ?.classList.replace("font-medium", "font-bold");
      item
        .querySelector(":scope > div > div:last-child")
        ?.classList.add("font-semibold");
      item.scrollIntoView({ block: "nearest" });
    }
  }
}
